import React, { useEffect, useRef, useState } from "react";
import { Languages, Check } from "lucide-react";
import { LANGUAGES } from "../../lib/i18n";
import { useLang } from "../../contexts/LanguageContext";

/**
 * Header-level language selector. Small pill button with a globe icon that
 * expands into a dropdown listing every supported language in its native form.
 */
export default function LanguageSelector() {
  const { lang, setLang, t } = useLang();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const current = LANGUAGES.find((l) => l.code === lang) || LANGUAGES[0];

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-2 h-9 px-2.5 rounded-full border border-white/10 hover:bg-white/[0.05] transition-colors focus-ring"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={t("nav.language")}
        title={t("nav.language")}
        data-testid="language-selector-btn"
      >
        <Languages className="w-3.5 h-3.5 text-white/80" />
        <span className="text-[12px] font-medium text-white/85 uppercase tracking-wider" data-testid="language-current-code">
          {current.code}
        </span>
      </button>

      {open && (
        <div
          className="absolute right-0 top-11 w-56 rounded-lg border border-white/10 bg-[#0F0F0F] shadow-2xl py-1.5 fade-in z-50"
          role="menu"
          data-testid="language-menu"
        >
          <div className="px-3 py-2 label-eyebrow text-white/50 border-b border-white/10 mb-1">
            {t("nav.language")}
          </div>
          {LANGUAGES.map((l) => {
            const active = l.code === lang;
            return (
              <button
                key={l.code}
                onClick={() => { setLang(l.code); setOpen(false); }}
                className={`w-full flex items-center justify-between gap-3 px-3 py-2 text-[13px] transition-colors ${
                  active
                    ? "bg-white/[0.05] text-white"
                    : "text-white/80 hover:bg-white/5"
                }`}
                data-testid={`language-option-${l.code}`}
                role="menuitem"
              >
                <span className="flex items-baseline gap-2 min-w-0">
                  <span className="font-medium truncate">{l.native}</span>
                  <span className="text-[10px] uppercase tracking-wider text-white/40">
                    {l.code}
                  </span>
                </span>
                {active && <Check className="w-3.5 h-3.5 text-white/70 flex-shrink-0" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
